export type CountdownUnitCase = "lower" | "upper";

/**
 * Relative kickoff duration. Zero units are omitted.
 * ≤24h: `5h 30m` | `5h` | `45m` | `1m`
 * >24h: `2d 5h` | `2d`
 */
export function formatCountdownDuration(
    ms: number,
    unitCase: CountdownUnitCase = "lower",
): string | null {
    if (!Number.isFinite(ms) || ms <= 0) return null;

    const totalDays = Math.floor(ms / (1000 * 60 * 60 * 24));
    const remainingAfterDays = ms % (1000 * 60 * 60 * 24);
    const totalHours = Math.floor(ms / (1000 * 60 * 60));
    const remainingAfterHours = ms % (1000 * 60 * 60);
    const hours = Math.floor(remainingAfterDays / (1000 * 60 * 60));
    const minutes = Math.floor(remainingAfterHours / (1000 * 60));

    const d = unitCase === "upper" ? "D" : "d";
    const h = unitCase === "upper" ? "H" : "h";
    const m = unitCase === "upper" ? "M" : "m";

    if (totalDays > 0) {
        return hours > 0 ? `${totalDays}${d} ${hours}${h}` : `${totalDays}${d}`;
    }
    if (totalHours > 0) {
        return minutes > 0 ? `${totalHours}${h} ${minutes}${m}` : `${totalHours}${h}`;
    }
    if (minutes > 0) return `${minutes}${m}`;
    return `1${m}`;
}

/** Imminent pre-start label: `in 1m` / `IN 1M` (not IN PLAY). */
export function formatImminentCountdown(
    ms: number,
    unitCase: CountdownUnitCase = "lower",
): string | null {
    const duration = formatCountdownDuration(ms, unitCase);
    if (!duration) return null;
    return unitCase === "upper" ? `IN ${duration}` : `in ${duration}`;
}

export function isImminentCountdownStatus(text: string | null | undefined): boolean {
    const t = (text ?? "").trim();
    return /^in\s+/i.test(t);
}

export type PrematchCountdownParts = {
    prefix: string;
    duration: string;
};

/** Ensure a visible word gap before the duration when feeds omit whitespace (`STARTS IN45m`). */
export function normalizePrematchCountdownSpacing(text: string | null | undefined): string {
    const trimmed = (text ?? "").trim();
    if (!trimmed) return trimmed;
    return trimmed
        .replace(/^((?:starts in|opens in|markets open in))(\d)/i, "$1 $2")
        .replace(/^in(\d)/i, "in $1");
}

/** Split pre-kickoff labels like `STARTS IN 1H 56M` / `Starts in 3h 40m` / `IN 14M` into prefix + duration. */
export function parsePrematchCountdownParts(text: string | null | undefined): PrematchCountdownParts | null {
    const trimmed = normalizePrematchCountdownSpacing(text);
    if (!trimmed) return null;

    const startsInMatch = trimmed.match(/^((?:starts in|opens in|markets open in))\s*(.+)$/i);
    if (startsInMatch) {
        const prefix = startsInMatch[1].trimEnd();
        return { prefix: `${prefix} `, duration: startsInMatch[2].trim() };
    }

    const imminentMatch = trimmed.match(/^(in)\s*(.+)$/i);
    if (imminentMatch && !/^in[\s-]?play$/i.test(trimmed)) {
        return { prefix: `${imminentMatch[1]} `, duration: imminentMatch[2].trim() };
    }

    return null;
}

export function isPrematchCountdownLabel(text: string | null | undefined): boolean {
    return parsePrematchCountdownParts(text) != null;
}
